import React from "react";
import CMSPanel from "@/components/admin/CMSPanel";
import {
  Field,
  TextInput,
  TextArea,
  NumberInput,
  StringListEditor,
  SchedulesEditor,
  PricesEditor,
} from "@/components/admin/fields";

const slugify = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const emptyCourse = () => ({
  id: "",
  title: "",
  tagline: "",
  short_description: "",
  description: "",
  duration: "",
  modality: "",
  image: "",
  modules: [],
  schedules: [],
  includes: [],
  prices: {},
  order: 99,
});

export function CoursesCMS({ branches }) {
  return (
    <CMSPanel
      entityKey="courses"
      publicPath="/courses"
      adminPath="/admin/courses"
      title="Cursos"
      itemLabel="Curso"
      branches={branches}
      emptyItem={emptyCourse}
      renderRow={(c) => (
        <div className="flex items-center gap-4">
          {c.image && (
            <img
              src={c.image}
              alt=""
              className="w-14 h-14 object-cover border border-black/10 shrink-0"
            />
          )}
          <div className="min-w-0">
            <div className="font-semibold text-[#0a0a0a] truncate">{c.title}</div>
            <div className="text-xs text-[#525252] truncate">
              {c.tagline || c.short_description || c.id}
            </div>
          </div>
        </div>
      )}
      renderForm={({ value, onChange, branches, mode }) => (
        <>
          <Field label="Título" required>
            <TextInput
              value={value.title}
              onChange={(v) => {
                const patch = { title: v };
                if (mode === "create" && !value.id) patch.id = slugify(v);
                onChange(patch);
              }}
              placeholder="Estilismo Profesional"
            />
          </Field>
          <Field
            label="ID (slug)"
            required
            hint="Identificador único en URL/precios. Usa solo minúsculas, números y guiones."
          >
            <TextInput
              value={value.id}
              onChange={(v) => onChange({ id: slugify(v) })}
              placeholder="estilismo"
            />
          </Field>
          <Field label="Tagline" hint="Aparece arriba del título en el modal del curso.">
            <TextInput
              value={value.tagline}
              onChange={(v) => onChange({ tagline: v })}
              placeholder="Tu propio salón de belleza"
            />
          </Field>
          <Field label="Descripción corta" hint="Aparece en la tarjeta de la sección Cursos.">
            <TextArea
              value={value.short_description}
              onChange={(v) => onChange({ short_description: v })}
              rows={2}
            />
          </Field>
          <Field label="Descripción larga" hint="Aparece en el modal del curso.">
            <TextArea
              value={value.description}
              onChange={(v) => onChange({ description: v })}
              rows={5}
            />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Duración">
              <TextInput
                value={value.duration}
                onChange={(v) => onChange({ duration: v })}
                placeholder="3 meses"
              />
            </Field>
            <Field label="Modalidad">
              <TextInput
                value={value.modality}
                onChange={(v) => onChange({ modality: v })}
                placeholder="Presencial · Lun-Sáb"
              />
            </Field>
          </div>
          <Field label="URL de imagen" hint="Pega la URL pública. Unsplash, Cloudinary, etc.">
            <TextInput
              value={value.image}
              onChange={(v) => onChange({ image: v })}
              placeholder="https://…"
            />
          </Field>
          <Field label="Orden" hint="Más bajo aparece primero.">
            <NumberInput value={value.order} onChange={(v) => onChange({ order: v ?? 99 })} step={1} />
          </Field>
          <Field label="Temario (módulos)">
            <StringListEditor
              items={value.modules}
              onChange={(v) => onChange({ modules: v })}
              placeholder="Nombre del módulo"
              addLabel="Agregar módulo"
            />
          </Field>
          <Field label="Lo que incluye">
            <StringListEditor
              items={value.includes}
              onChange={(v) => onChange({ includes: v })}
              placeholder="Beneficio incluido"
              addLabel="Agregar incluye"
            />
          </Field>
          <Field label="Turnos / Horarios">
            <SchedulesEditor
              items={value.schedules}
              onChange={(v) => onChange({ schedules: v })}
            />
          </Field>
          <Field label="Precios por sucursal" hint="Define el precio semanal o marca como Consultar / No disponible.">
            <PricesEditor
              branches={branches}
              value={value.prices || {}}
              onChange={(v) => onChange({ prices: v })}
            />
          </Field>
        </>
      )}
    />
  );
}

const emptyBranch = () => ({
  id: "",
  name: "",
  address: "",
  phone: "",
  whatsapp: "",
  hours: "",
  lat: null,
  lng: null,
  order: 99,
});

export function BranchesCMS() {
  return (
    <CMSPanel
      entityKey="branches"
      publicPath="/branches"
      adminPath="/admin/branches"
      title="Sucursales"
      itemLabel="Sucursal"
      emptyItem={emptyBranch}
      renderRow={(b) => (
        <div className="min-w-0">
          <div className="font-semibold text-[#0a0a0a] truncate">{b.name}</div>
          <div className="text-xs text-[#525252] truncate">{b.address}</div>
          <div className="mt-0.5 text-[0.65rem] uppercase tracking-[0.18em] text-[#525252]">
            id: {b.id} · WhatsApp: {b.whatsapp || "—"}
          </div>
        </div>
      )}
      renderForm={({ value, onChange, mode }) => (
        <>
          <Field label="Nombre" required>
            <TextInput
              value={value.name}
              onChange={(v) => {
                const patch = { name: v };
                if (mode === "create" && !value.id) patch.id = slugify(v);
                onChange(patch);
              }}
              placeholder="Mayorazgo"
            />
          </Field>
          <Field label="ID (slug)" required hint="Identificador único. Lo usan los precios de los cursos.">
            <TextInput
              value={value.id}
              onChange={(v) => onChange({ id: slugify(v) })}
              placeholder="mayorazgo"
            />
          </Field>
          <Field label="Dirección">
            <TextArea
              value={value.address}
              onChange={(v) => onChange({ address: v })}
              rows={2}
            />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Teléfono">
              <TextInput
                value={value.phone}
                onChange={(v) => onChange({ phone: v })}
                placeholder="+52 222 622 8842"
              />
            </Field>
            <Field label="WhatsApp (solo dígitos)" hint="Formato wa.me, ej. 522226228842">
              <TextInput
                value={value.whatsapp}
                onChange={(v) => onChange({ whatsapp: v.replace(/\D/g, "") })}
                placeholder="522226228842"
              />
            </Field>
          </div>
          <Field label="Horario">
            <TextInput
              value={value.hours}
              onChange={(v) => onChange({ hours: v })}
              placeholder="Lun-Vie: 9:00 - 19:00 · Sáb: 9:00 - 18:00"
            />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Latitud" hint="Para mapa y cálculo de distancia.">
              <NumberInput
                value={value.lat}
                onChange={(v) => onChange({ lat: v })}
                placeholder="19.05135"
              />
            </Field>
            <Field label="Longitud">
              <NumberInput
                value={value.lng}
                onChange={(v) => onChange({ lng: v })}
                placeholder="-98.19600"
              />
            </Field>
          </div>
          <Field label="Orden" hint="Más bajo aparece primero.">
            <NumberInput value={value.order} onChange={(v) => onChange({ order: v ?? 99 })} step={1} />
          </Field>
        </>
      )}
    />
  );
}

const emptyTestimonial = () => ({
  name: "",
  course: "",
  image: "",
  rating: 5,
  date: "",
  quote: "",
  order: 99,
});

export function TestimonialsCMS() {
  return (
    <CMSPanel
      entityKey="testimonials"
      publicPath="/testimonials"
      adminPath="/admin/testimonials"
      title="Testimonios"
      itemLabel="Testimonio"
      emptyItem={emptyTestimonial}
      renderRow={(t) => (
        <div className="flex items-center gap-4">
          {t.image && (
            <img
              src={t.image}
              alt=""
              className="w-11 h-11 object-cover rounded-full border border-black/10 shrink-0"
            />
          )}
          <div className="min-w-0">
            <div className="font-semibold text-[#0a0a0a] truncate">
              {t.name}{" "}
              <span className="text-[#525252] font-normal text-xs">· {t.rating}★</span>
            </div>
            <div className="text-xs text-[#525252] line-clamp-1 max-w-md">
              {t.quote}
            </div>
          </div>
        </div>
      )}
      renderForm={({ value, onChange }) => (
        <>
          <Field label="Nombre" required>
            <TextInput
              value={value.name}
              onChange={(v) => onChange({ name: v })}
              placeholder="María Fernanda L."
            />
          </Field>
          <Field label="Curso (opcional)" hint="Aparece debajo del nombre.">
            <TextInput
              value={value.course}
              onChange={(v) => onChange({ course: v })}
              placeholder="Estilismo Profesional"
            />
          </Field>
          <Field label="URL imagen de perfil">
            <TextInput
              value={value.image}
              onChange={(v) => onChange({ image: v })}
              placeholder="https://…"
            />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Rating (1-5)" required>
              <NumberInput
                value={value.rating}
                onChange={(v) => onChange({ rating: Math.max(1, Math.min(5, v || 5)) })}
                step={1}
                min={1}
                max={5}
              />
            </Field>
            <Field label="Fecha (texto)" hint='Ej: "hace 1 mes"'>
              <TextInput
                value={value.date}
                onChange={(v) => onChange({ date: v })}
                placeholder="hace 1 semana"
              />
            </Field>
          </div>
          <Field label="Reseña" required>
            <TextArea
              value={value.quote}
              onChange={(v) => onChange({ quote: v })}
              rows={4}
            />
          </Field>
          <Field label="Orden" hint="Más bajo aparece primero en el slider.">
            <NumberInput value={value.order} onChange={(v) => onChange({ order: v ?? 99 })} step={1} />
          </Field>
        </>
      )}
    />
  );
}

const emptyGalleryItem = () => ({ url: "", category: "Estilismo", order: 99 });
const GALLERY_CATEGORIES = ["Estilismo", "Barbería", "Uñas", "Pestañas"];

export function GalleryCMS() {
  return (
    <CMSPanel
      entityKey="gallery"
      publicPath="/gallery"
      adminPath="/admin/gallery"
      title="Galería"
      itemLabel="Imagen"
      emptyItem={emptyGalleryItem}
      renderRow={(g) => (
        <div className="flex items-center gap-4">
          {g.url && (
            <img
              src={g.url}
              alt=""
              className="w-14 h-14 object-cover border border-black/10 shrink-0"
            />
          )}
          <div className="min-w-0">
            <div className="font-semibold text-[#0a0a0a] text-sm">{g.category}</div>
            <div className="text-[0.65rem] uppercase tracking-[0.18em] text-[#525252] truncate max-w-md">
              {g.url}
            </div>
          </div>
        </div>
      )}
      renderForm={({ value, onChange }) => (
        <>
          <Field label="URL de la imagen" required>
            <TextInput
              value={value.url}
              onChange={(v) => onChange({ url: v })}
              placeholder="https://images.unsplash.com/…"
            />
          </Field>
          {value.url && (
            <div className="border border-black/10 p-2 bg-[#fafafa]">
              <img
                src={value.url}
                alt=""
                className="w-full aspect-video object-cover"
              />
            </div>
          )}
          <Field label="Categoría" required>
            <select
              value={value.category}
              onChange={(e) => onChange({ category: e.target.value })}
              className="w-full bg-white border border-black/15 px-3 py-2.5 text-sm focus:outline-none focus:border-[#c40000]"
            >
              {GALLERY_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Orden" hint="Más bajo aparece primero.">
            <NumberInput value={value.order} onChange={(v) => onChange({ order: v ?? 99 })} step={1} />
          </Field>
        </>
      )}
    />
  );
}
